"use client";

import React, { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import { useRouter } from "next/navigation";
import { useCart } from "@/context/CartContext";

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { addToCart } = useCart();
  const [addedToCart, setAddedToCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0 }
    );
    const els = sectionRef?.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const handleAddToCart = async () => {
    setCartLoading(true);
    try {
      const res = await fetch("/api/shopify/products");
      const data = await res?.json();
      const product = data?.products?.[0];
      const variant = product?.variants?.edges?.[0]?.node;
      if (variant) {
        addToCart(
          {
            id: variant?.id,
            variantId: variant?.id,
            label: product?.title,
            price: parseFloat(variant?.price?.amount),
            image: product?.images?.edges?.[0]?.node?.url,
            imageAlt: product?.images?.edges?.[0]?.node?.altText ?? product?.title,
          },
          1
        );
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 2500);
      }
    } catch {
      // silently fail
    } finally {
      setCartLoading(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center pt-2 overflow-hidden bg-pmu-cream">
      {/* Marquee strip at the top of the hero section */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden bg-pmu-amber py-2.5">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 12 })?.map((_, i) => (
            <span key={i} className="inline-flex items-center gap-4 px-6 text-pmu-espresso font-fraunces font-bold text-sm tracking-widest uppercase">
              NEW FLAVOURS COMING SOON
              <span className="text-pmu-espresso opacity-50">✦</span>
            </span>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full py-10 sm:py-14 lg:py-20">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-20 items-center">

          {/* Left: Text content */}
          <div className="space-y-6 sm:space-y-8">
            {/* Eyebrow */}
            <div className="reveal-hidden">
              <span className="stat-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-pmu-amber inline-block" />
                100% Vegan · Gluten-Free 
              </span>
            </div>

            {/* Headline */}
            <div className="reveal-hidden delay-1">
              <h1
                className="font-fraunces font-black text-pmu-espresso leading-none"
                style={{ fontSize: "clamp(2.25rem, 6vw, 5.5rem)", letterSpacing: "-0.02em" }}>

                <span style={{ color: "#A8C4D8" }}>
                  <img
                    src="/assets/images/Pick_Me_Up-branding_logomark-blue-11-1773749660255.png"
                    alt="Pick Me Up"
                    style={{ height: "clamp(2.25rem, 6vw, 5.5rem)", width: "auto", display: "inline-block" }} />

                </span>
                <br />
                <span className="italic font-light text-pmu-taupe" style={{ fontSize: "clamp(1.1rem, 3vw, 2.5rem)", letterSpacing: "0" }}>the cookie for you to be your best self

                </span>
              </h1>
            </div>

            {/* Body */}
            <div className="reveal-hidden delay-2">
              <p className="text-sm sm:text-base md:text-lg text-pmu-taupe leading-relaxed max-w-md" style={{ fontWeight: 400 }}>
                With <span className="text-pmu-espresso font-medium">9g of plant-based protein</span>{" "}
                and <span className="text-pmu-espresso font-medium">4g of fiber</span>, Pick Me Up cookies are vegan and gluten free — finally, a cookie my fitness trainer would be proud of.
              </p>
            </div>

            {/* CTAs */}
            <div className="reveal-hidden delay-3 flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
              <button className="btn-primary w-full sm:w-auto justify-center" onClick={() => router?.push("/purchase")}>
                Order Now
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
              <button
                className="btn-secondary w-full sm:w-auto justify-center"
                onClick={handleAddToCart}
                disabled={cartLoading || addedToCart}
              >
                {cartLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    Adding…
                  </span>
                ) : addedToCart ? (
                  <span className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Added!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 01-8 0" />
                    </svg>
                    Add to Cart
                  </span>
                )}
              </button>
              <button className="btn-secondary w-full sm:w-auto justify-center" onClick={() => router?.push("/our-story")}>
                Our Story
              </button>
            </div>

            {/* Minimal stats row */}
            <div className="reveal-hidden delay-4 flex items-center gap-5 sm:gap-8 pt-4">
              <div>
                <p className="font-fraunces font-bold text-2xl sm:text-3xl text-pmu-espresso leading-none">9g</p>
                <p className="text-xs text-pmu-taupe mt-1 tracking-wider uppercase" style={{ letterSpacing: "0.1em" }}>Protein</p>
              </div>
              <div className="w-px h-10 bg-pmu-stone" />
              <div>
                <p className="font-fraunces font-bold text-2xl sm:text-3xl text-pmu-espresso leading-none">4g</p>
                <p className="text-xs text-pmu-taupe mt-1 tracking-wider uppercase" style={{ letterSpacing: "0.1em" }}>Fibre</p>
              </div>
              <div className="w-px h-10 bg-pmu-stone" />
              <div>
                <p className="font-fraunces font-bold text-2xl sm:text-3xl text-pmu-espresso leading-none">0</p>
                <p className="text-xs text-pmu-taupe mt-1 tracking-wider uppercase" style={{ letterSpacing: "0.1em" }}>Dairy</p>
              </div>
            </div>
          </div>

          {/* Right: Single clean image */}
          <div className="reveal-hidden delay-2 relative flex items-center justify-center mt-4 md:mt-0">
            <div className="relative w-full flex items-center justify-center">
              <AppImage
                src="/assets/images/PickMeUp_cookie_cream_bg_compressed-1774454998186.jpg"
                alt="Top-down view of a thick golden-brown oat and chickpea chocolate chip cookie on a clean white surface"
                width={700}
                height={620}
                className="object-contain w-full h-auto max-h-[620px]"
                style={{ mixBlendMode: "multiply" }}
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority />

            </div>

            {/* Floating minimal badge */}
            <div
              className="absolute -bottom-5 -left-5 bg-white rounded-2xl px-5 py-4 shadow-card hidden sm:block"
              style={{ border: "1px solid rgba(214, 207, 196, 0.5)" }}>

              <p className="text-xs text-pmu-taupe uppercase tracking-widest mb-1" style={{ letterSpacing: "0.12em" }}>Made with</p>
              <p className="font-fraunces font-semibold text-pmu-espresso text-sm">Chickpeas &amp; Oats</p>
            </div>
          </div>
        </div>
      </div>
      {/* Marquee strip at the bottom of the hero section */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden bg-pmu-amber py-2.5">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 12 })?.map((_, i) => (
            <span key={i} className="inline-flex items-center gap-4 px-6 text-pmu-espresso font-fraunces font-bold text-sm tracking-widest uppercase">
              NEW FLAVOURS COMING SOON
              <span className="text-pmu-espresso opacity-50">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );

}